// The web's Store, as the kit's Store.swift is the apps': the account as the screens see it, every action the user can
// take (each through the core's rules), and Undo and Redo. It knows no React (hooks.ts connects it) and no Firestore
// (a `StoreBackend`, the sync, writes what changed).
//
// - The account shown is the backend's: changes from elsewhere replace it, and the store's own changes go to the
//   backend at once, which keeps them in its copy, so nothing waits for Firestore's echo.
// - Undo puts back only the records an action touched, as they were before it; what changed elsewhere meanwhile
//   stays. A change from elsewhere that reaches records of a step waiting to be undone or redone drops that step,
//   so undoing can't undo what someone else did.
// - Below the account's minimum feature level the store changes nothing (status "blocked": the page asks for a
//   reload).

import { addDays, at, currentZone, today as todayIn } from "../core/days";
import { combineEdits, isNoEdit, noEdits, type Edits, type RuleContext } from "../core/edits";
import {
  containsUnknownValues, accountMarks, markForShares, markForTodoChange, suggestsUpdate, type FeatureMark,
} from "../core/featureLevel";
import { FREE_HISTORY_DAYS, Lists, storageKey, type ArchiveFilter, type CompletedShown, type ListOptions, type ListSection, type Route } from "../core/lists";
import { orderAtEnd } from "../core/order";
import { splitEmoji, withAreaIcon } from "../core/areaIcons";
import { useTemplate, type TemplateWhen } from "../core/templates";
import {
  canRestToday, changeSchedule, dayTodo, doneDaysOf, restsToday, formerHabit, habitDayOf, habitProgress, isDue, lateDay, newMilestone, periodTick, refreshHabitDays, repeatOfSchedule,
  sameSchedule, scheduleFromRepeat, sortedHabits, ticksWhileOff, type HabitPeriodTick, type HabitProgress, type LateHabitTick,
} from "../core/habits";
import { MARK_FOR_HABIT } from "../core/featureLevel";
import { parse, type ParserContext } from "../core/parser";
import type { LabelLanguage } from "../core/parser/labels";
import {
  makeArea, makeHabit, makeProject, sameValue, type Area, type Favorite, type Habit, type HabitSchedule, type Project, type ProjectKind,
  type Section, type Share, type Template, type Todo,
} from "../core/records";
import { codecs } from "../core/records";
import { completeProject, duplicateProject, skipOccurrence, toggleCompletion } from "../core/repeats";
import {
  adoptLooseProjects, convertToProject, deleteSection, deleteTodos, keep, moveArea, moveNextTo, moveProject, moveSection,
  moveTodo, moveToSection, moveToSectionEnd, moveToSectionTop, moveToTodaySection, newSection, newTodo, noteInboxExit, noteReminderZones, noteSeriesReminder,
  losesReminder, updateProject, updateTodo, updateTodos, type MoveDestination,
} from "../core/actions";
import { mergeTodoDraft } from "../core/changes";
import { nextSyncChange, syncStatus, type SyncObservation, type SyncStatus } from "../core/syncStatus";
import { canMove, canWrite, isOwner, ShareRouting, type ShareableCollection } from "../core/sharing";
import type { Day, Start, Weekday } from "../core/types";
import {
  ACCOUNT_COLLECTIONS, applyChanges, byID, changesOf, emptyAccount, isNoChange, touchedIDs, type Account, type AccountCollection,
  type Changes,
} from "./account";
import { applyQuickEntry } from "./quickEntry";

/** What keeps the account in step: the sync (sync.ts), or anything that behaves like it. */
export interface StoreBackend {
  /** What the browser kept of the account; null when it never synced it. */
  load(): Promise<Account | null>;
  /** Starts listening; `onChange` gets the whole account when it changed elsewhere. */
  start(onChange: (account: Account) => void): void;
  /** Writes a change: `snapshot` is everything after it, `previous` everything as the store knew it before. */
  apply(changes: Changes, snapshot: Account, previous: Account): void;
  /** Marks the account as needing a feature level (only ever raised). */
  mark(mark: FeatureMark): void;
  /** Marks the account: its areas got their icons (`areaIconsAddedAt`, docs/61). */
  markAreaIconsAdded(): void;
  stop(): void;
  /** This version may not take part (the account's minimum feature level). */
  readonly isBlocked: boolean;
  /** How the browser stands with the server (docs/64); absent for a backend with nothing to sync. */
  readonly syncObservation?: SyncObservation;
  /** Calls `listener` whenever `syncObservation` changed. */
  subscribeSync?(listener: () => void): () => void;
  /** Try Again: listens anew; resolves whether the browser is in step now. */
  retrySync?(): Promise<boolean>;
}

/** "loading": nothing to show yet (the first visit waits for every collection); "blocked": the page asks for a reload. */
export type StoreStatus = "loading" | "ready" | "blocked";

/** What the screens read; a new object whenever anything in it changed (for `useSyncExternalStore`). */
export interface StoreState {
  status: StoreStatus;
  account: Account;
  /** The browser's date. */
  today: Day;
  canUndo: boolean;
  canRedo: boolean;
  /** The kit's names for the step ("Completed", "To-do deleted"), for the screens to translate. */
  undoTitle?: string;
  redoTitle?: string;
  /** A newer version would show more of the account (its marks, or values the web doesn't know). */
  suggestsUpdate: boolean;
  /** What the cloud in the list's bar says (docs/64); null while the browser is in step. */
  sync: SyncStatus | null;
}

export interface StoreOptions {
  uid: string;
  backend: StoreBackend;
  now?: () => Date;
  zone?: () => string;
  /** 2 = Monday (the default), 1 = Sunday. */
  firstWeekday?: Weekday;
  newID?: () => string;
  /** The language of Quick Entry's chips. */
  language?: LabelLanguage;
  /** The name of the area made for projects that have none, in the person's content language ("Projects"). */
  looseProjectsTitle?: () => string;
  /** How many steps Undo keeps. */
  undoLimit?: number;
}

/** Settings of the lists that live in the browser (the screens keep them, as the apps keep them per device). */
export interface ListSettings {
  staleAfterDays?: number;
  todayGroupsByProject?: boolean;
  /** Habits in a group of their own at the top of Today (docs/63). */
  habitsInOwnGroup?: boolean;
  /** Today keeps its empty sections (Settings › Lists, TodayLayout.showsEmptySections). */
  todayShowsEmptySections?: boolean;
  /** Today sections made in this browser since the page opened that no to-do has gone into (kept by the store itself). */
  newTodaySections?: ReadonlySet<string>;
  /** Just completed to-dos that stay visible for a moment before they leave the list. */
  lingering?: ReadonlySet<string>;
  /** The Archive narrowed to one project or area (the Archive's filter; not stored). */
  archiveFilter?: ArchiveFilter;
}

type TouchedRecords = { [Name in AccountCollection]?: Map<string, Account[Name][number] | undefined> };

interface UndoStep {
  title: string;
  before: TouchedRecords;
  after: TouchedRecords;
  favorites?: { before: Favorite[] | undefined; after: Favorite[] | undefined };
}

export interface CommitOptions {
  /** The step's name for Undo; without one (or with `undoable: false`) the change can't be undone. */
  title?: string;
  undoable?: boolean;
  marks?: readonly FeatureMark[];
}

export class Store {
  readonly uid: string;
  private readonly backend: StoreBackend;
  private readonly options: StoreOptions;
  private account: Account = emptyAccount();
  private loaded = false;
  private adopted = false;
  /** Whether this start looked at the areas' icons already (`addAreaIcons`). */
  private areaIconsChecked = false;
  /** The habits whose tick the morning after asked today, by habit and day: it asks once a day (docs/70). */
  private readonly lateTicksAsked = new Set<string>();
  private state: StoreState;
  private readonly listeners = new Set<() => void>();
  private undoSteps: UndoStep[] = [];
  private redoSteps: UndoStep[] = [];
  /** The step a transaction gathers into. */
  private openStep: UndoStep | null = null;
  private transactionDepth = 0;
  private settings: ListSettings = {};
  private listsCache: { key: unknown[]; lists: Lists } | null = null;
  private dayTimer: ReturnType<typeof setInterval> | undefined;
  private syncTimer: ReturnType<typeof setTimeout> | undefined;
  private stopSync: (() => void) | null = null;
  private started = false;

  constructor(options: StoreOptions) {
    this.options = options;
    this.uid = options.uid;
    this.backend = options.backend;
    this.state = this.makeState();
  }

  // MARK: - Starting and stopping

  /** Shows what the browser kept, then listens. Resolves once the kept account (if any) is shown. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    const kept = await this.backend.load();
    if (kept && !this.loaded) {
      this.account = kept;
      this.loaded = true;
      this.adoptLooseProjects();
      this.refreshHabitDaysQuietly();
      this.emit();
    }
    this.backend.start((remote) => this.take(remote));
    this.stopSync = this.backend.subscribeSync?.(this.followSync) ?? null;
    this.followSync();
    // The page coming back to the front: a new day may have begun, and with it the habits' days.
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", this.visible);
    // A new day changes what Today holds.
    this.dayTimer = setInterval(() => this.refreshDay(), 30_000);
  }

  private readonly visible = () => {
    if (document.visibilityState === "visible") this.refreshDay();
  };

  stop(): void {
    clearInterval(this.dayTimer);
    clearTimeout(this.syncTimer);
    this.stopSync?.();
    this.stopSync = null;
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", this.visible);
    this.backend.stop();
    this.listeners.clear();
  }

  // MARK: - Reading

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): StoreState => this.state;

  get status(): StoreStatus {
    return this.state.status;
  }

  // MARK: - In step with the server (docs/64)

  private syncNow(): SyncStatus | null {
    const observation = this.backend.syncObservation;
    if (!observation) return null;
    return syncStatus(observation, this.backend.isBlocked, (this.options.now ?? (() => new Date()))().getTime());
  }

  /** The rule on what the sync knows now, again when a wait runs out (`nextSyncChange`); the screens hear a change. */
  private readonly followSync = (): void => {
    clearTimeout(this.syncTimer);
    const observation = this.backend.syncObservation;
    if (!observation) return;
    const now = (this.options.now ?? (() => new Date()))().getTime();
    const next = nextSyncChange(observation, now);
    if (next !== null) this.syncTimer = setTimeout(this.followSync, next - now + 50);
    const status = this.syncNow();
    const shown = this.state.sync;
    if (status?.kind !== shown?.kind || status?.since !== shown?.since) this.emit();
  };

  /** Try Again, in the cloud's popover: resolves whether the browser is in step now (then the cloud goes). */
  async retrySync(): Promise<boolean> {
    const inStep = (await this.backend.retrySync?.()) ?? true;
    this.followSync();
    return inStep;
  }

  get current(): Account {
    return this.account;
  }

  /** What the core's rules need: today, now, the zone, the week's first day, new ids. */
  context(): RuleContext {
    const zone = (this.options.zone ?? currentZone)();
    const now = (this.options.now ?? (() => new Date()))();
    const context: RuleContext = { today: todayIn(zone, now), now, zone, firstWeekday: this.options.firstWeekday ?? 2 };
    if (this.options.newID) context.newID = this.options.newID;
    return context;
  }

  /** Every list of the account now (made again when the records, the day or the settings change). */
  lists(): Lists {
    const context = this.context();
    const historyDays = this.account.user.premiumActive === true ? undefined : FREE_HISTORY_DAYS;
    const key = [this.account, context.today, this.settings, historyDays];
    if (this.listsCache && this.listsCache.key.every((value, index) => value === key[index])) return this.listsCache.lists;
    const options: ListOptions = { today: context.today, zone: context.zone, now: context.now, ...this.settings };
    if (historyDays !== undefined) options.historyDays = historyDays;
    const lists = new Lists(this.account, options);
    this.listsCache = { key, lists };
    return lists;
  }

  /** The blocks of one list, as its screen shows them. */
  list(route: Route, completed: CompletedShown = "none"): ListSection[] {
    return this.lists().sections(route, completed);
  }

  setListSettings(settings: ListSettings): void {
    this.settings = { ...this.settings, ...settings };
    this.emit();
  }

  /** Called on a timer and when the page comes back: a new day makes new lists. */
  refreshDay(): void {
    if (this.context().today === this.state.today) return;
    // A new day: yesterday's open habit days are missed, today's come (Store.refreshHabitDays).
    if (!this.refreshHabitDays()) this.emit();
  }

  record<Name extends AccountCollection>(collection: Name, id: string | undefined): Account[Name][number] | undefined {
    if (id === undefined) return undefined;
    return (this.account[collection] as readonly { id: string }[]).find((record) => record.id === id) as Account[Name][number] | undefined;
  }

  todo(id: string | undefined): Todo | undefined { return this.record("todos", id); }
  project(id: string | undefined): Project | undefined { return this.record("projects", id); }
  area(id: string | undefined): Area | undefined { return this.record("areas", id); }
  section(id: string | undefined): Section | undefined { return this.record("sections", id); }
  template(id: string | undefined): Template | undefined { return this.record("templates", id); }
  habit(id: string | undefined): Habit | undefined { return this.record("habits", id); }

  /** The share a record lives in, or null for the person's own. */
  shareOf(collection: ShareableCollection, record: Todo | Project | Area | Section): Share | null {
    const shareID = new ShareRouting(this.account.shares, this.account.projects).of(collection, record);
    return shareID === null ? null : this.account.shares.find((share) => share.id === shareID) ?? null;
  }

  /** Whether the person may change the record where it lives (the Firestore rules): in a share, to-dos always, the rest as owner. */
  canWrite(collection: ShareableCollection, record: Todo | Project | Area | Section): boolean {
    return canWrite(collection, this.shareOf(collection, record)?.id ?? null, this.account.shares, this.uid);
  }

  /** Whether the person owns what the record is in: only the owner deletes, renames or reorders what is shared. */
  isOwner(collection: ShareableCollection, record: Todo | Project | Area | Section): boolean {
    return isOwner(this.shareOf(collection, record)?.id ?? null, this.account.shares, this.uid);
  }

  /** Every tag in use, for Quick Entry and the tag picker. */
  get tags(): string[] {
    const tags = new Set<string>();
    for (const todo of this.account.todos) for (const tag of todo.tags) tags.add(tag);
    for (const project of this.account.projects) for (const tag of project.tags ?? []) tags.add(tag);
    for (const area of this.account.areas) for (const tag of area.tags ?? []) tags.add(tag);
    return [...tags].sort((a, b) => a.localeCompare(b));
  }

  /** What the parser needs to read "#tag" and "@project" against this account. */
  parserContext(): ParserContext {
    const context: ParserContext = {
      today: this.context().today,
      tags: this.tags,
      projects: this.account.projects.map((project) => ({ id: project.id, title: project.title, completed: project.completedAt !== undefined })),
      areas: this.account.areas.map((area) => ({ id: area.id, title: area.title })),
      firstWeekday: this.options.firstWeekday ?? 2,
      // A habit from the words only while one can be made: three free, then Pro (Store.parserContext).
      habits: this.account.user.premiumActive === true || sortedHabits(this.account.habits).length < 3,
    };
    if (this.options.language) context.language = this.options.language;
    return context;
  }

  // MARK: - Changing

  /**
   * Applies a change, writes it, and keeps it for Undo when it has a title. Returns false when nothing could be
   * changed (blocked, or nothing loaded yet).
   */
  commit(changes: Changes, options: CommitOptions = {}): boolean {
    if (this.state.status !== "ready") return false;
    for (const mark of options.marks ?? []) this.backend.mark(mark);
    if (isNoChange(changes)) {
      if ((options.marks ?? []).length > 0) this.emit();
      return true;
    }
    const previous = this.account;
    changes = withReminderZones(changes, previous, this.context().zone);
    const next = applyChanges(previous, changes);
    this.account = next;
    this.forgetUsedNewTodaySections();
    if (options.undoable !== false && (options.title !== undefined || this.openStep)) this.remember(options.title ?? "", changes, previous, next);
    this.backend.apply(changes, next, previous);
    this.emit();
    return true;
  }

  /** The core's edits, with their marks. */
  commitEdits(edits: Edits, title?: string): boolean {
    if (isNoEdit(edits)) return false;
    const options: CommitOptions = { marks: edits.marks };
    if (title !== undefined) options.title = title;
    return this.commit(changesOf(edits), options);
  }

  /** Runs several changes as one step of Undo (editing a to-do from opening to closing, say). */
  transaction<T>(title: string, body: () => T): T {
    if (this.transactionDepth === 0) this.openStep = { title, before: {}, after: {} };
    this.transactionDepth += 1;
    try {
      return body();
    } finally {
      this.transactionDepth -= 1;
      if (this.transactionDepth === 0) {
        const step = this.openStep;
        this.openStep = null;
        if (step && (Object.keys(step.before).length > 0 || step.favorites)) this.pushUndo(step);
        this.emit();
      }
    }
  }

  undo(): boolean {
    const step = this.undoSteps.pop();
    if (!step) return false;
    this.restore(step.before, step.favorites?.before, step.favorites !== undefined);
    this.redoSteps.push(step);
    this.emit();
    return true;
  }

  redo(): boolean {
    const step = this.redoSteps.pop();
    if (!step) return false;
    this.restore(step.after, step.favorites?.after, step.favorites !== undefined);
    this.undoSteps.push(step);
    this.emit();
    return true;
  }

  // MARK: - To-dos

  /** A new to-do that fits the list it was added from (Store.addTodo); `after` is the id of the to-do it goes below. */
  addTodo(route: Route, options: { title?: string; notes?: string; sectionID?: string; atEnd?: boolean; after?: string } = {}): Todo | null {
    const todo = this.newTodoIn(route, options);
    if (options.title !== undefined) todo.title = options.title;
    if (options.notes !== undefined) todo.notes = options.notes;
    return this.commit({ todos: { saved: [todo], deleted: [] } }, { title: "To-do added" }) ? todo : null;
  }

  /**
   * A new to-do from Quick Entry: what the parser recognised in the text (a day, a time, a repeat, tags, a project or
   * area) taken over, on top of what the list gives. `ignoring` are the tokens the user took back.
   */
  quickAdd(text: string, route: Route, options: { sectionID?: string; atEnd?: boolean; after?: string; ignoring?: Iterable<string>; notes?: string } = {}): Todo | null {
    const context = this.context();
    const base = this.newTodoIn(route, options);
    base.title = text.trim();
    if (options.notes !== undefined) base.notes = options.notes;
    const parserContext = this.parserContext();
    if (options.ignoring) parserContext.ignoring = options.ignoring;
    const applied = applyQuickEntry(base, parse(text, parserContext), context);
    const todo = noteSeriesReminder(base, noteInboxExit(base, applied, context.now));
    const mark = markForTodoChange(base, todo);
    return this.commit({ todos: { saved: [todo], deleted: [] } }, { title: "To-do added", marks: mark ? [mark] : [] }) ? todo : null;
  }

  /** A to-do changed (Store.update): its Inbox exit and series reminder noted; the account marked when a repeat got an end. */
  updateTodo(next: Todo, title = "To-do changed"): boolean {
    const old = this.todo(next.id);
    if (!old) {
      // A habit's card on a day it isn't due (habitCardDay): what was changed is the habit's; the day isn't stored.
      const base = this.isUnstoredHabitDay(next) ? this.habitCardDay(next.habitID!) : undefined;
      const followed = base && this.followHabit(base, next);
      return followed ? this.commitHabits([followed], [], [], title) : false;
    }
    // Someday takes the reminder away; Undo says so and brings both back (Store.update).
    const edits = updateTodo(old, next, this.context().now);
    const followed = this.followHabit(old, next);
    if (!followed) return this.commitEdits(edits, losesReminder(old, next) ? "Reminder removed" : title);
    return this.commit({ ...changesOf(edits), habits: { saved: [followed], deleted: [] } },
      { title: losesReminder(old, next) ? "Reminder removed" : title, marks: edits.marks });
  }

  /**
   * A habit's day edited in its card (Store.followHabit): the title, notes, area and the series' reminder are the
   * habit's, so the habit takes them and its next days have them too; the day's own reminder (This One) stays the day's. Undefined when nothing of the habit changes.
   */
  private followHabit(old: Todo, next: Todo): Habit | undefined {
    const habit = this.habit(next.habitID);
    if (!habit) return undefined;
    const followed: Habit = { ...habit };
    if (next.title !== old.title) followed.title = next.title;
    if (next.notes !== old.notes) followed.notes = next.notes;
    if (next.areaID !== old.areaID) { if (next.areaID === undefined) delete followed.areaID; else followed.areaID = next.areaID; }
    // The habit's time is the day's series' time (docs/71): Future Ones, or the first reminder (also one typed in the
    // title of a day without any). This One stays with the day.
    const first = old.reminder === undefined && old.seriesReminder === undefined && next.seriesReminder === undefined && next.reminder !== undefined;
    const series = first ? next.reminder : next.seriesReminder;
    const seriesZone = first ? next.reminderZone : next.seriesReminderZone;
    if (first || next.seriesReminder?.getTime() !== old.seriesReminder?.getTime()) {
      if (series === undefined) { delete followed.reminder; delete followed.reminderZone; }
      else { followed.reminder = series; followed.reminderZone = seriesZone ?? this.context().zone; }
    }
    return sameValue(codecs.habits.encode(followed), codecs.habits.encode(habit)) ? undefined : followed;
  }

  /** Several to-dos changed at once (one step). */
  updateTodos(next: readonly Todo[], title = "To-dos changed"): boolean {
    const pairs = next.flatMap((todo) => {
      const old = this.todo(todo.id);
      return old ? [{ old, next: todo }] : [];
    });
    const loses = pairs.some(({ old, next }) => losesReminder(old, next));
    return this.commitEdits(updateTodos(pairs, this.context().now), loses ? "Reminder removed" : title);
  }

  /**
   * Saves the editor's draft: the fields the user changed since `base` (the to-do when editing began) go over what is
   * stored now, so a change from elsewhere that arrived meanwhile survives (core/changes.ts: mergeTodoDraft).
   */
  saveDraft(draft: Todo, base: Todo, title = "To-do changed"): boolean {
    const stored = this.todo(draft.id);
    if (!stored) return false;
    return this.updateTodo(mergeTodoDraft(draft, base, stored), title);
  }

  /** Ticks a to-do off, or opens it again (a repeating one adds or takes back its next one). */
  toggleCompletion(id: string): boolean {
    const todo = this.todo(id);
    if (!todo) return false;
    const context = { ...this.context(), ...(this.shareOf("todos", todo) ? { completedBy: this.uid } : {}) };
    let edits = toggleCompletion(todo, this.account.todos, context);
    if (todo.habitID !== undefined && todo.completedAt !== undefined) {
      // A habit's day counted anyway goes again: open, it isn't due (Store.toggleCompletion). In the same step.
      const after = applyChanges(this.account, changesOf(edits));
      const refreshed = refreshHabitDays(after.habits, after.todos, context.today, context.zone, context.firstWeekday);
      edits = combineEdits(edits, { ...noEdits(), todos: refreshed });
    }
    return this.commitEdits(edits, todo.completedAt === undefined ? "Completed" : "Reopened");
  }

  deleteTodos(ids: readonly string[]): boolean {
    const todos = ids.flatMap((id) => this.todo(id) ?? []);
    if (todos.length === 0) return false;
    return this.commitEdits(deleteTodos(todos, this.context().now), todos.length === 1 ? "To-do deleted" : "To-dos deleted");
  }

  /** With the Move picker: to the Inbox, Miscellaneous, an area or a project. */
  moveTodos(ids: readonly string[], destination: MoveDestination): boolean {
    const now = this.context().now;
    const edits = ids.reduce((all, id) => {
      const todo = this.todo(id);
      return todo ? combineEdits(all, moveTodo(todo, destination, now)) : all;
    }, noEdits());
    return this.commitEdits(edits, "Moved");
  }

  /** To the end of another section of its project; undefined: out of every section. */
  moveToSection(id: string, sectionID: string | undefined): boolean {
    const todo = this.todo(id);
    const moved = todo && moveToSection(todo, sectionID, this.orders());
    return moved ? this.updateTodo(moved, "Moved") : false;
  }

  /** Let go at the end of a section, after what it shows (`shown`): also those already in it go to its end. */
  moveToSectionEnd(ids: readonly string[], sectionID: string | undefined, shown: readonly string[], inToday: boolean): boolean {
    const moved = moveToSectionEnd(ids, sectionID, shown, this.account.todos, inToday, this.settings.lingering);
    return moved.length ? this.updateTodos(moved, "Moved") : false;
  }

  /** Let go on a section's heading, above what it shows (`shown`): also those already in it go to its top. */
  moveToSectionTop(ids: readonly string[], sectionID: string | undefined, shown: readonly string[], inToday: boolean): boolean {
    const moved = moveToSectionTop(ids, sectionID, shown, this.account.todos, inToday, this.settings.lingering);
    return moved.length ? this.updateTodos(moved, "Moved") : false;
  }

  /** To the end of a Today section, or out of all of them. */
  moveToTodaySection(id: string, sectionID: string | undefined): boolean {
    const todo = this.todo(id);
    const moved = todo && moveToTodaySection(todo, sectionID, this.orders());
    return moved ? this.updateTodo(moved, "Moved") : false;
  }

  /** Dragged right above or below another to-do; in Today it joins that one's Today section. */
  moveNextTo(id: string, targetID: string, place: "before" | "after", inToday: boolean): boolean {
    const todo = this.todo(id);
    const target = this.todo(targetID);
    const moved = todo && target && moveNextTo(todo, target, place, this.orders(), inToday);
    return moved ? this.updateTodo(moved, "Moved") : false;
  }

  /** Changes the start of to-dos (When: Today, a day, Anytime, Someday, the Inbox). */
  schedule(ids: readonly string[], start: Start): boolean {
    return this.updateTodos(ids.flatMap((id) => {
      const todo = this.todo(id);
      return todo ? [{ ...todo, start }] : [];
    }), "Scheduled");
  }

  /** Skips this time of a repeating to-do. */
  skip(id: string): boolean {
    const todo = this.todo(id);
    const edits = todo && skipOccurrence(todo, this.context());
    return edits ? this.commitEdits(edits, "Skipped") : false;
  }

  /** Kept in the Stale review; with `start`, moved there as well. */
  keep(id: string, start?: Start): boolean {
    const todo = this.todo(id);
    return todo ? this.commitEdits(keep(todo, this.context().now, start), "Kept") : false;
  }

  /** Makes a to-do into a project in an area; returns the project. */
  convertToProject(id: string, areaID: string): Project | null {
    const todo = this.todo(id);
    const area = this.area(areaID);
    if (!todo || !area) return null;
    const { edits, project } = convertToProject(todo, area, this.account, this.context());
    return this.commitEdits(edits, "Made into a project") ? project : null;
  }

  // MARK: - Projects

  addProject(title: string, areaID: string, kind: ProjectKind = "project"): Project | null {
    const project = makeProject({ id: this.newID(), title, areaID, order: orderAtEnd(this.account.projects.map((other) => other.order)) });
    // A plain project keeps no kind, as in older data.
    if (kind !== "project") project.kind = kind;
    return this.commit({ projects: { saved: [project], deleted: [] } }, { title: "Project added" }) ? project : null;
  }

  /** A project changed; a move into an area it may not go to (a project shared on its own into a shared area) keeps the old area. */
  updateProject(next: Project, title = "Project changed"): boolean {
    const old = this.project(next.id);
    if (!old) return false;
    const canMoveTo = (areaID: string) => canMove(next, areaID, this.account.shares, this.account.areas, this.account.projects);
    return this.commitEdits(updateProject(old, next, canMoveTo), title);
  }

  /** Completes a project with everything open in it; a repeating one comes back as its next copy. */
  completeProject(id: string): boolean {
    const project = this.project(id);
    if (!project) return false;
    return this.commitEdits(completeProject(project, this.account, this.context()), project.kind === "list" ? "List completed" : "Project completed");
  }

  duplicateProject(id: string): Project | null {
    const project = this.project(id);
    if (!project) return null;
    const copy = duplicateProject(project, this.account, this.context());
    const changed = this.commit({
      projects: { saved: [copy.project], deleted: [] }, sections: { saved: copy.sections, deleted: [] }, todos: { saved: copy.todos, deleted: [] },
    }, { title: "Project duplicated" });
    return changed ? copy.project : null;
  }

  /** Deletes a project with its to-dos and sections (Store.delete(project)). */
  deleteProject(id: string): boolean {
    const project = this.project(id);
    if (!project) return false;
    return this.commit({
      projects: { saved: [], deleted: [id] },
      todos: { saved: [], deleted: this.account.todos.filter((todo) => todo.projectID === id).map((todo) => todo.id) },
      sections: { saved: [], deleted: this.account.sections.filter((section) => section.projectID === id).map((section) => section.id) },
    }, { title: project.kind === "list" ? "List deleted" : "Project deleted" });
  }

  /** Puts a project right above another of an area, or at its end, moving it into that area if need be. */
  moveProject(id: string, before: string | undefined, areaID: string): boolean {
    const project = this.project(id);
    if (!project) return false;
    const canMoveTo = (area: string) => canMove(project, area, this.account.shares, this.account.areas, this.account.projects);
    const moved = moveProject(project, before, areaID, this.account.projects, canMoveTo);
    return moved ? this.commit({ projects: { saved: moved, deleted: [] } }, { title: "Moved" }) : false;
  }

  // MARK: - Templates

  /**
   * Makes a project from a template (Store.use): in `areaID` (none: the template's own area); when that area doesn't
   * exist, the first area, else a new one. One step of Undo.
   */
  useTemplate(templateID: string, title: string, areaID: string | undefined, when: TemplateWhen): Project | null {
    const template = this.template(templateID);
    if (!template) return null;
    const firstArea = [...this.account.areas].sort((a, b) => a.order - b.order)[0];
    let area = this.area(areaID ?? template.areaID) ?? firstArea;
    let newArea: Area | undefined;
    if (!area) {
      newArea = makeArea({ id: this.newID(), title: withAreaIcon(this.options.looseProjectsTitle?.() ?? "Projects"), order: orderAtEnd([]) });
      area = newArea;
    }
    const result = useTemplate(template, { title, area, when }, this.account, this.context());
    const changes: Changes = { ...changesOf(result.edits), templates: { saved: [result.template], deleted: [] } };
    if (newArea) changes.areas = { saved: [newArea], deleted: [] };
    return this.commit(changes, { title: "Project made from template" }) ? result.project : null;
  }

  // MARK: - Habits (docs/63; the kit's Store, "Habits (docs/63)")

  /** The habits that run, in the user's order. */
  get habits(): Habit[] {
    return sortedHabits(this.account.habits);
  }

  /** Its numbers today: streak, best, today, this week's or month's count. */
  habitProgress(habit: Habit): HabitProgress {
    const context = this.context();
    return habitProgress(habit, doneDaysOf(habit.id, this.account.todos, context.zone), context.today, context.zone, context.firstWeekday);
  }

  /** Whether Not Today is offered for it today (canRestToday; docs/63 15d). */
  canRestHabitToday(habitID: string): boolean {
    const habit = this.habit(habitID);
    if (!habit) return false;
    const context = this.context();
    return canRestToday(habit, doneDaysOf(habit.id, this.account.todos, context.zone), context.today, context.zone, context.firstWeekday);
  }

  /** Whether it is off today with Not Today (restsToday): its rows say "Not today", Back to Today is offered. */
  restsHabitToday(habitID: string): boolean {
    const habit = this.habit(habitID);
    if (!habit) return false;
    const context = this.context();
    return restsToday(habit, doneDaysOf(habit.id, this.account.todos, context.zone), context.today, context.zone, context.firstWeekday);
  }

  /**
   * What a tick of a flexible habit's day did to its week or month (periodTick; the toast, boards 9 and 10): undefined
   * for a to-do, a habit on set days, or a day not done or outside the current week or month.
   */
  habitPeriodTick(todoID: string): HabitPeriodTick | undefined {
    const todo = this.account.todos.find((other) => other.id === todoID);
    const habit = todo?.habitID === undefined ? undefined : this.habit(todo.habitID);
    if (!todo || !habit) return undefined;
    const context = this.context();
    const day = habitDayOf(todo, context.zone);
    if (day === undefined) return undefined;
    return periodTick(habit, doneDaysOf(habit.id, this.account.todos, context.zone), day, context.today, context.zone, context.firstWeekday);
  }

  /** The days it was done. */
  habitDoneDays(habitID: string): Set<string> {
    return doneDaysOf(habitID, this.account.todos, this.context().zone);
  }

  /** Today's to-do of a habit, if it has one. */
  habitTodayTodo(habitID: string): Todo | undefined {
    const { today, zone } = this.context();
    return this.account.todos.find((todo) => todo.habitID === habitID && habitDayOf(todo, zone) === today);
  }

  /**
   * What a habit's card opens on in Habits and an area's habits (Store.cardDay): today's to-do, or, on a day it isn't
   * due, today's to-do as it would be, not stored. Its card then changes the habit, and ticking it counts the day
   * anyway (`countHabitToday`).
   */
  habitCardDay(habitID: string): Todo | undefined {
    const habit = this.habit(habitID);
    if (!habit) return undefined;
    return this.habitTodayTodo(habitID) ?? dayTodo(habit, this.context().today, this.context().zone);
  }

  /** A habit's day that isn't stored: the card of a habit on a day it isn't due (`habitCardDay`). */
  isUnstoredHabitDay(todo: Todo): boolean {
    return todo.habitID !== undefined && this.todo(todo.id) === undefined;
  }

  /**
   * Done today although today isn't planned (Store.countHabitToday; more than planned, Bastian 2026-10-26): its day is
   * stored done. A flexible habit counts it for its week or month; one on set days shows it, and its streak doesn't
   * count it (docs/63 section 8: days off don't exist for the streak). Unticked, the day goes again (toggleCompletion).
   */
  countHabitToday(day: Todo): boolean {
    if (day.habitID === undefined || !this.habit(day.habitID) || this.habitTodayTodo(day.habitID)) return false;
    const todo: Todo = { ...day, completedAt: this.context().now };
    if (this.shareOf("todos", todo)) todo.completedBy = this.uid;
    return this.commit({ todos: { saved: [todo], deleted: [] } }, { title: "Completed" });
  }

  /**
   * A habit's card closing (Store.finishEditing): a cleared title is the habit's name again; on a day it isn't due,
   * what was changed goes to the habit and no day is stored; a new habit closed without a name (New in Habits, "+ Habit") goes with its days, with no Undo,
   * as nothing was lost; otherwise the day is saved as any to-do.
   */
  finishHabitCard(draft: Todo, title = "To-do changed"): boolean {
    const habit = this.habit(draft.habitID);
    if (!habit) return this.updateTodo(draft, title);
    // A named habit's day keeps the habit's name when its title is cleared: only a new habit without one goes.
    if (habit.title.trim() !== "" && draft.title.trim() === "") draft = { ...draft, title: habit.title };
    if (this.isUnstoredHabitDay(draft)) return this.updateTodo(draft, title);
    if (draft.title.trim() === "" && draft.notes.trim() === "" && habit.title.trim() === "") {
      // Its making goes from Undo too, so Redo can't bring back a habit without a name.
      const ofHabit = (step: UndoStep) => step.after.habits?.has(habit.id) === true;
      this.undoSteps = this.undoSteps.filter((step) => !ofHabit(step));
      this.redoSteps = this.redoSteps.filter((step) => !ofHabit(step));
      return this.commit({
        habits: { saved: [], deleted: [habit.id] },
        todos: { saved: [], deleted: this.account.todos.filter((todo) => todo.habitID === habit.id).map((todo) => todo.id) },
      }, { undoable: false });
    }
    return this.updateTodo(draft, title);
  }

  /** A new habit, with its to-do for today when it is due today (New in Habits, "+ Habit" in an area). */
  addHabit(title: string, options: { areaID?: string; schedule?: HabitSchedule; notes?: string; neverMissTwice?: boolean } = {}): Habit | null {
    const habit = makeHabit({
      id: this.newID(), title: title.trim(), notes: options.notes ?? "", schedule: options.schedule ?? { kind: "everyNDays", interval: 1 },
      startsOn: this.context().today, order: orderAtEnd(this.account.habits.map((other) => other.order)),
    });
    if (options.areaID !== undefined) habit.areaID = options.areaID;
    if (options.neverMissTwice === false) habit.neverMissTwice = false;
    return this.commitHabits([habit], [], [], "Habit added", true) ? habit : null;
  }

  /** The habit changed (its title, notes, area, schedule, rule, reminder, breaks): today's open to-do follows. */
  updateHabit(next: Habit, title = "Habit changed"): boolean {
    const old = this.habit(next.id);
    if (!old) return false;
    let habit: Habit = { ...next };
    // A new How Often counts from today on; the past keeps the rule it had (changeSchedule).
    if (!sameSchedule(habit.schedule, old.schedule) && sameValue(habit.earlierSchedules, old.earlierSchedules)) {
      const { today, zone, firstWeekday } = this.context();
      habit = changeSchedule({ ...habit, schedule: old.schedule }, habit.schedule, today, zone, firstWeekday);
    }
    if (habit.reminder === undefined) delete habit.reminderZone;
    else if (habit.reminder.getTime() !== old.reminder?.getTime() && habit.reminderZone === old.reminderZone) habit.reminderZone = this.context().zone;
    return this.commitHabits([habit], [], [], title);
  }

  /**
   * The flame in a to-do's card (docs/63 section 6): the to-do becomes a habit and today's day of it. Its title, notes,
   * area and reminder go to the habit; its repeat becomes How Often; When, the deadline and its project go. A repeating
   * to-do brings its past completions along as the habit's history. One step of Undo.
   *
   * A to-do that was a habit (Make a To-Do, docs/63 15e) brings that habit back instead (formerHabit; Bastian,
   * 2026-10-29): the same record with its days, as if it had never been off. The days the to-do (or a copy its repeat
   * made) was ticked meanwhile become its days too (ticksWhileOff), so they count as done; the days off without a tick
   * count as not done, and the usual rules decide what is left of the streak (Store.makeHabit).
   */
  makeHabitFrom(todoID: string, schedule?: HabitSchedule): Habit | null {
    const todo = this.todo(todoID);
    if (!todo || todo.habitID !== undefined) return null;
    const { today, zone, firstWeekday } = this.context();
    const areaID = todo.areaID ?? this.project(todo.projectID)?.areaID;
    const history: Todo[] = [];
    const chain = [todo.id];
    const seen = new Set([todo.id]);
    for (let previous = todo.spawnedFrom; previous !== undefined && !seen.has(previous);) {
      seen.add(previous);
      const found = this.todo(previous);
      if (!found) break;
      chain.push(found.id);
      if (found.completedAt !== undefined && found.habitID === undefined) history.push(found);
      previous = found.spawnedFrom;
    }
    const reminder = todo.seriesReminder ?? todo.reminder;
    const reminderZone = todo.seriesReminder !== undefined ? todo.seriesReminderZone : todo.reminderZone;
    const made: Todo = { ...todo, start: { kind: "on", day: today } };
    for (const key of ["deadline", "repeats", "seriesReminder", "seriesReminderZone", "projectID", "sectionID"] as const) delete made[key];
    if (areaID !== undefined) made.areaID = areaID;
    const former = formerHabit(chain, this.account.habits);
    if (former) {
      // Back with its history; what the to-do says now goes to it, and a repeat changed meanwhile becomes its How Often
      // from today, the past keeping the rule it had.
      let back: Habit = { ...former, title: todo.title, notes: todo.notes };
      delete back.archivedAt;
      delete back.becameTodoID;
      if (areaID !== undefined) back.areaID = areaID; else delete back.areaID;
      if (reminder) {
        back.reminder = reminder;
        if (reminderZone !== undefined) back.reminderZone = reminderZone; else delete back.reminderZone;
      } else {
        delete back.reminder;
        delete back.reminderZone;
      }
      const next = schedule ?? scheduleFromRepeat(todo.repeats, todo.start);
      if (next && !sameSchedule(next, back.schedule)) back = changeSchedule(back, next, today, zone, firstWeekday);
      made.habitID = back.id;
      // The days it was ticked as a to-do meanwhile count: those to-dos become its days.
      const ticked = ticksWhileOff(former, this.account.todos, today, zone)
        .filter((id) => id !== todo.id).map((id) => this.todo(id)).filter((one): one is Todo => one !== undefined)
        .map((one) => ({ ...one, habitID: back.id }));
      return this.commitHabits([back], [made, ...ticked], [], "Made a habit", true) ? back : null;
    }
    const days = history.map((done) => habitDayOf(done, zone)).filter((day): day is NonNullable<typeof day> => day !== undefined).sort();
    const habit = makeHabit({
      id: this.newID(), title: todo.title, notes: todo.notes, schedule: schedule ?? scheduleFromRepeat(todo.repeats, todo.start) ?? { kind: "everyNDays", interval: 1 },
      startsOn: days[0] !== undefined && days[0] < today ? days[0] : today, order: orderAtEnd(this.account.habits.map((other) => other.order)),
    });
    if (areaID !== undefined) habit.areaID = areaID;
    if (reminder) {
      habit.reminder = reminder;
      if (reminderZone !== undefined) habit.reminderZone = reminderZone;
    }
    made.habitID = habit.id;
    const saved = [made, ...history.map((done) => ({ ...done, habitID: habit.id }))];
    return this.commitHabits([habit], saved, [], "Made a habit", true) ? habit : null;
  }

  /**
   * The flame tapped again: the habit stops (archived, its history kept); today's to-do stays, How Often as its repeat,
   * and the habit remembers it (becameTodoID), so the flame on it later brings the habit back.
   * On a day off (no open day) the to-do starts on the next day the habit is planned (today if none within a year), so
   * its title, notes, area and reminder go on. Returns the to-do's id.
   */
  turnIntoTodo(habitID: string): string | undefined {
    const habit = this.habit(habitID);
    if (!habit) return undefined;
    const { today, zone, now, firstWeekday } = this.context();
    const archived: Habit = { ...habit, archivedAt: now };
    let open = this.account.todos.find((todo) => todo.habitID === habitID && todo.completedAt === undefined);
    if (!open) {
      const done = doneDaysOf(habitID, this.account.todos, zone);
      let next = today;
      for (let ahead = 1; ahead <= 366; ahead++) {
        const day = addDays(today, ahead);
        if (isDue(habit, day, done, zone, firstWeekday)) { next = day; break; }
      }
      open = { ...dayTodo(habit, next, zone), id: this.newID(), createdAt: now };
    }
    const saved: Todo[] = [];
    if (open) {
      // The flame on this to-do, or on a later copy of it, brings the habit back (makeHabitFrom).
      archived.becameTodoID = open.id;
      const plain: Todo = { ...open };
      delete plain.habitID;
      const repeats = repeatOfSchedule(habit.schedule);
      if (repeats) {
        plain.repeats = repeats;
        if (plain.reminder) {
          plain.seriesReminder = plain.reminder;
          if (plain.reminderZone !== undefined) plain.seriesReminderZone = plain.reminderZone;
        }
      }
      saved.push(plain);
    }
    const made = this.commit({ habits: { saved: [archived], deleted: [] }, todos: { saved, deleted: [] } }, { title: "Made a to-do" });
    return made ? open.id : undefined;
  }

  /** Deletes a habit with its history and today's to-do. One step of Undo. */
  deleteHabit(habitID: string): boolean {
    if (!this.habit(habitID)) return false;
    return this.commit({
      habits: { saved: [], deleted: [habitID] },
      todos: { saved: [], deleted: this.account.todos.filter((todo) => todo.habitID === habitID).map((todo) => todo.id) },
    }, { title: "Habit deleted" });
  }

  /** A break from today until `until` (both included): today's open to-do goes, the streak is frozen. */
  pauseHabit(habitID: string, until: string): boolean {
    const habit = this.habit(habitID);
    if (!habit) return false;
    const today = this.context().today;
    const pauses = (habit.pauses ?? []).filter((pause) => (pause.until ?? "9999-12-31") < today);
    pauses.push({ from: today, until: (until < today ? today : until) as typeof today });
    return this.updateHabit({ ...habit, pauses }, "Habit paused");
  }

  /** Ends the current break: from today the habit is due again. */
  resumeHabit(habitID: string): boolean {
    const habit = this.habit(habitID);
    if (!habit?.pauses) return false;
    const today = this.context().today;
    const yesterday = addDays(today, -1);
    const pauses = habit.pauses.flatMap((pause) => {
      const from = pause.from ?? "0000-01-01";
      const until = pause.until ?? "9999-12-31";
      if (!(from <= today && until >= today) && !(from > today)) return [pause];
      return from <= yesterday ? [{ ...pause, until: yesterday }] : [];
    });
    const next: Habit = { ...habit };
    if (pauses.length) next.pauses = pauses; else delete next.pauses;
    return this.updateHabit(next, "Habit resumed");
  }

  /** Not Today for a flexible habit (N times a week or month): off today without a miss, while canRestHabitToday allows. */
  restHabitToday(habitID: string): boolean {
    const habit = this.habit(habitID);
    if (!habit || !this.canRestHabitToday(habitID)) return false;
    return this.updateHabit({ ...habit, restsOn: this.context().today }, "Not Today");
  }

  /** Back to Today (Store.unrestHabitToday): takes Not Today back, and its day is in Today again at once. Only while
   *  restsHabitToday. */
  unrestHabitToday(habitID: string): boolean {
    const habit = this.habit(habitID);
    if (!habit || !this.restsHabitToday(habitID)) return false;
    const next: Habit = { ...habit };
    delete next.restsOn;
    return this.updateHabit(next, "Back to Today");
  }

  /**
   * Before a habit's day today is ticked (Store.lateTick(beforeTicking:); docs/70, Bastian 2026-11-03: "the question
   * must come first"): whether the tick asks "yesterday or today?" first. The day is open (stored, or a day off's not
   * stored), yesterday was a planned day not done (`lateDay`), and this habit hasn't asked today. Asking is noted.
   */
  lateHabitTick(todo: Todo): LateHabitTick | undefined {
    const habit = todo.habitID === undefined ? undefined : this.habit(todo.habitID);
    if (!habit || todo.completedAt !== undefined || habit.title.trim() === "") return undefined;
    const { today, zone, firstWeekday } = this.context();
    if (habitDayOf(todo, zone) !== today) return undefined;
    const day = lateDay(habit, doneDaysOf(habit.id, this.account.todos, zone), today, zone, firstWeekday);
    if (day === undefined) return undefined;
    const key = `${habit.id}-${today}`;
    if (this.lateTicksAsked.has(key)) return undefined;
    this.lateTicksAsked.add(key);
    return { todoID: todo.id, habitID: habit.id, title: habit.title, day };
  }

  /**
   * "Yesterday" (Store.countForYesterday; docs/70): the tick counts for the day before. Yesterday's day is stored done
   * (at noon, as the page's month does), today's opens again, or goes on a day it isn't due. One step of Undo.
   */
  countForYesterday(late: LateHabitTick): boolean {
    const habit = this.habit(late.habitID);
    if (!habit) return false;
    const { today, zone } = this.context();
    const saved: Todo[] = [];
    const deleted: string[] = [];
    const ticked = this.todo(late.todoID);
    if (ticked?.completedAt !== undefined) {
      if (isDue(habit, today, doneDaysOf(habit.id, this.account.todos, zone), zone, this.context().firstWeekday)) {
        const reopened: Todo = { ...ticked };
        delete reopened.completedAt;
        delete reopened.completedBy;
        saved.push(reopened);
      } else {
        deleted.push(ticked.id);
      }
    }
    const existing = this.account.todos.find((todo) => todo.habitID === habit.id && habitDayOf(todo, zone) === late.day);
    if (existing?.completedAt === undefined) {
      const when = at(late.day, 12, 0, zone);
      saved.push(existing ? { ...existing, completedAt: when } : dayTodo(habit, late.day, zone, when));
    }
    const habits = (habit.startsOn ?? today) > late.day ? [{ ...habit, startsOn: late.day }] : [];
    return this.commit({ todos: { saved, deleted }, habits: { saved: habits, deleted: [] } }, { title: "Counted for yesterday" });
  }

  /** The page's month: a past day marked done or not (the user's own record); a day before its start moves the start. */
  setHabitDay(habitID: string, day: string, done: boolean): boolean {
    const habit = this.habit(habitID);
    const { today, zone, now } = this.context();
    if (!habit || day > today) return false;
    const existing = this.account.todos.find((todo) => todo.habitID === habitID && habitDayOf(todo, zone) === day);
    if (done) {
      if (existing?.completedAt !== undefined) return false;
      // A past day is done on that day (at noon), so it stays its history and never shows among today's completed.
      const when = day === today ? now : at(day as typeof today, 12, 0, zone);
      const todo = existing ? { ...existing, completedAt: when } : dayTodo(habit, day as typeof today, zone, when);
      const habits = (habit.startsOn ?? today) > day ? [{ ...habit, startsOn: day as typeof today }] : [];
      return this.commit({ todos: { saved: [todo], deleted: [] }, habits: { saved: habits, deleted: [] } }, { title: "Habit changed" });
    }
    if (!existing) return false;
    if (day === today) {
      const reopened: Todo = { ...existing };
      delete reopened.completedAt;
      delete reopened.completedBy;
      return this.commit({ todos: { saved: [reopened], deleted: [] } }, { title: "Habit changed" });
    }
    return this.commit({ todos: { saved: [], deleted: [existing.id] } }, { title: "Habit changed" });
  }

  /** The milestone the last tick reached that has no card yet, if any (Store.newMilestone). */
  newHabitMilestone(habitID: string): number | undefined {
    const habit = this.habit(habitID);
    if (!habit) return undefined;
    const progress = this.habitProgress(habit);
    return progress.isDoneToday ? newMilestone(progress.streak, progress.unit, habit.milestonesShown ?? []) : undefined;
  }

  noteMilestoneShown(habitID: string, milestone: number): boolean {
    const habit = this.habit(habitID);
    if (!habit) return false;
    const shown = [...new Set([...(habit.milestonesShown ?? []), milestone])].sort((a, b) => a - b);
    return this.commit({ habits: { saved: [{ ...habit, milestonesShown: shown }], deleted: [] } }, { undoable: false });
  }

  /** Moves a habit before another in Habits (undefined: to the end). */
  moveHabit(habitID: string, before: string | undefined): boolean {
    const moving = this.habit(habitID);
    if (!moving) return false;
    const ordered = this.habits.filter((habit) => habit.id !== habitID);
    const place = before === undefined ? ordered.length : Math.max(0, ordered.findIndex((habit) => habit.id === before));
    ordered.splice(place === -1 ? ordered.length : place, 0, moving);
    const saved = ordered.flatMap((habit, index) => (habit.order === index + 1 ? [] : [{ ...habit, order: index + 1 }]));
    return this.commit({ habits: { saved, deleted: [] } }, { title: "Moved" });
  }

  /**
   * Moves a habit right after another (Store.moveHabit(_:after:)): before the one that follows `target` in the user's
   * order without the moved one, or to the end. Where a drop lands after the last of a group in Habits' lists.
   */
  moveHabitAfter(habitID: string, target: string): boolean {
    const others = this.habits.filter((habit) => habit.id !== habitID);
    const index = others.findIndex((habit) => habit.id === target);
    if (index === -1) return false;
    return this.moveHabit(habitID, others[index + 1]?.id);
  }

  /**
   * Brings the habits' to-dos up to date (core/habits.ts: refreshHabitDays): at the start, with every change from
   * elsewhere, at a new day and when the page comes back. Not undoable; true when something changed.
   */
  refreshHabitDays(): boolean {
    if (this.state.status !== "ready" && !(this.loaded && !this.backend.isBlocked)) return false;
    const { today, zone, firstWeekday } = this.context();
    const { saved, deleted } = refreshHabitDays(this.account.habits, this.account.todos, today, zone, firstWeekday);
    if (saved.length === 0 && deleted.length === 0) return false;
    const changes: Changes = { todos: { saved, deleted } };
    const previous = this.account;
    this.account = applyChanges(previous, changes);
    this.backend.apply(changes, this.account, previous);
    this.emit();
    return true;
  }

  /** A change of habits and to-dos, with today's days brought up to date in the same step. */
  private commitHabits(habits: Habit[], todos: Todo[], deletedTodos: string[], title: string, marks = false): boolean {
    const { today, zone, firstWeekday } = this.context();
    const after = applyChanges(this.account, { habits: { saved: habits, deleted: [] }, todos: { saved: todos, deleted: deletedTodos } });
    const refreshed = refreshHabitDays(after.habits, after.todos, today, zone, firstWeekday);
    const savedTodos = new Map(todos.map((todo) => [todo.id, todo]));
    for (const todo of refreshed.saved) savedTodos.set(todo.id, todo);
    const deleted = new Set([...deletedTodos, ...refreshed.deleted]);
    for (const id of deleted) savedTodos.delete(id);
    return this.commit({ habits: { saved: habits, deleted: [] }, todos: { saved: [...savedTodos.values()], deleted: [...deleted] } },
      { title, marks: marks ? [MARK_FOR_HABIT] : [] });
  }

  // MARK: - Areas

  /** A new area at the end. Every area has an icon (docs/61): a title without one gets the suggestion for its name. */
  addArea(title: string): Area | null {
    const area = makeArea({ id: this.newID(), title: withAreaIcon(title.trim()), order: orderAtEnd(this.account.areas.map((other) => other.order)) });
    return this.commit({ areas: { saved: [area], deleted: [] } }, { title: "Area added" }) ? area : null;
  }

  updateArea(next: Area, title = "Area changed"): boolean {
    if (!this.area(next.id)) return false;
    return this.commit({ areas: { saved: [next], deleted: [] } }, { title });
  }

  /** Deletes an area with its projects and to-dos (Store.delete(area)). */
  deleteArea(id: string): boolean {
    if (!this.area(id)) return false;
    const projects = new Set(this.account.projects.filter((project) => project.areaID === id).map((project) => project.id));
    return this.commit({
      areas: { saved: [], deleted: [id] },
      projects: { saved: [], deleted: [...projects] },
      todos: { saved: [], deleted: this.account.todos.filter((todo) => todo.areaID === id || (todo.projectID !== undefined && projects.has(todo.projectID))).map((todo) => todo.id) },
      sections: { saved: [], deleted: this.account.sections.filter((section) => section.projectID !== undefined && projects.has(section.projectID)).map((section) => section.id) },
    }, { title: "Area deleted" });
  }

  moveArea(id: string, before: string | undefined): boolean {
    const area = this.area(id);
    if (!area) return false;
    const moved = moveArea(area, before, this.account.areas);
    return this.commit({ areas: { saved: moved, deleted: [] } }, { title: "Moved" });
  }

  // MARK: - Sections

  /** A new section at the end of a project, or of Today without one. */
  addSection(title: string, projectID?: string): Section | null {
    const context = this.context();
    const section = newSection(title, projectID, this.account.sections, context.newID ? { newID: context.newID } : {});
    // Today hides its empty sections; one just made shows until a to-do goes into it (Store.newTodaySectionIDs).
    if (projectID === undefined) this.settings = { ...this.settings, newTodaySections: new Set([...(this.settings.newTodaySections ?? []), section.id]) };
    return this.commit({ sections: { saved: [section], deleted: [] } }, { title: "Section added" }) ? section : null;
  }

  /** Today was left: the sections just made there are sections like the others from now on (Store.leftToday). */
  leftToday(): void {
    if (!this.settings.newTodaySections?.size) return;
    this.setListSettings({ newTodaySections: new Set() });
  }

  /** A new Today section that a to-do went into is a section like the others from now on (Lists.isNewTodaySection). */
  private forgetUsedNewTodaySections(): void {
    const fresh = this.settings.newTodaySections;
    if (fresh === undefined || fresh.size === 0) return;
    const used = new Set(this.account.todos.flatMap((todo) => (todo.todaySectionID !== undefined && fresh.has(todo.todaySectionID) ? [todo.todaySectionID] : [])));
    if (used.size === 0) return;
    this.settings = { ...this.settings, newTodaySections: new Set([...fresh].filter((id) => !used.has(id))) };
  }

  updateSection(next: Section, title = "Section changed"): boolean {
    if (!this.section(next.id)) return false;
    return this.commit({ sections: { saved: [next], deleted: [] } }, { title });
  }

  /** Deletes a section; its to-dos stay, out of it. */
  deleteSection(id: string): boolean {
    const section = this.section(id);
    return section ? this.commitEdits(deleteSection(section, this.account.todos), "Section deleted") : false;
  }

  moveSection(id: string, before: string | undefined): boolean {
    const section = this.section(id);
    if (!section) return false;
    return this.commit({ sections: { saved: moveSection(section, before, this.account.sections), deleted: [] } }, { title: "Moved" });
  }

  // MARK: - Favorites

  /** Pins a list, project or area to Home, or takes it off. Nothing while the favorites aren't known. */
  toggleFavorite(route: Route): boolean {
    const favorite = favoriteOf(route, this.account);
    if (!favorite || this.account.favorites === undefined) return false;
    const favorites = this.account.favorites;
    const index = favorites.findIndex((other) => sameValue(other, favorite));
    return this.setFavorites(index === -1 ? [...favorites, favorite] : favorites.filter((_, at) => at !== index));
  }

  setFavorites(favorites: Favorite[]): boolean {
    return this.commit({ favorites }, { title: "Favorites changed" });
  }

  // MARK: - Inside

  private newID(): string {
    return (this.options.newID ?? (() => crypto.randomUUID().toUpperCase()))();
  }

  private orders(): number[] {
    return this.account.todos.map((todo) => todo.order);
  }

  private newTodoIn(route: Route, options: { sectionID?: string; atEnd?: boolean; after?: string }): Todo {
    const after = this.todo(options.after);
    const placement: { sectionID?: string; atEnd?: boolean; after?: Todo } = {};
    if (options.sectionID !== undefined) placement.sectionID = options.sectionID;
    if (options.atEnd !== undefined) placement.atEnd = options.atEnd;
    if (after) placement.after = after;
    return newTodo(route, this.orders(), this.context(), placement);
  }

  /** Changes from elsewhere replace what is shown. */
  private take(remote: Account): void {
    // A share this browser didn't know: versions before sharing can't show it, so the account suggests the update.
    const mark = markForShares(this.account.shares.map((share) => share.id), remote.shares);
    if (mark && !this.backend.isBlocked) this.backend.mark(mark);
    this.dropSteps(remote);
    this.account = remote;
    this.loaded = true;
    this.adoptLooseProjects();
    this.addAreaIcons();
    // A habit made or changed elsewhere, or the first answer of the account: today's days follow.
    if (!this.backend.isBlocked) this.refreshHabitDaysQuietly();
    this.emit();
  }

  /**
   * Every area has an icon (docs/61; Store.addAreaIconsIfNeeded in the kit): once per account, each area of the person's
   * own whose title starts with no emoji gets the suggestion for its name, else 🗂️; only the titles change. Areas
   * shared by others stay as their owner keeps them. Only with the server's whole first answer (`take`), where the
   * person's document has said whether another device did it already; then the account is marked, so that another
   * device, or an area renamed later by an older version, is left alone. Not undoable.
   */
  private addAreaIcons(): void {
    if (this.areaIconsChecked || this.backend.isBlocked || this.account.user.areaIconsAdded) return;
    this.areaIconsChecked = true;
    const saved = this.account.areas
      .filter((area) => splitEmoji(area.title).emoji === undefined && this.isOwner("areas", area))
      .map((area) => ({ ...area, title: withAreaIcon(area.title) }));
    if (saved.length > 0) {
      const changes: Changes = { areas: { saved, deleted: [] } };
      const previous = this.account;
      this.account = applyChanges(previous, changes);
      this.backend.apply(changes, this.account, previous);
    }
    this.backend.markAreaIconsAdded();
  }

  /** `refreshHabitDays` without its own emit, inside `take` and `start`, which emit themselves. */
  private refreshHabitDaysQuietly(): void {
    const { today, zone, firstWeekday } = this.context();
    const { saved, deleted } = refreshHabitDays(this.account.habits, this.account.todos, today, zone, firstWeekday);
    if (saved.length === 0 && deleted.length === 0) return;
    const changes: Changes = { todos: { saved, deleted } };
    const previous = this.account;
    this.account = applyChanges(previous, changes);
    this.backend.apply(changes, this.account, previous);
  }

  /** Every project lives in an area: older data may hold projects without one (Store.adoptLooseProjects), once per start. */
  private adoptLooseProjects(): void {
    if (this.adopted || this.backend.isBlocked) return;
    this.adopted = true;
    const context = this.context();
    const edits = adoptLooseProjects(this.account.projects, this.account.areas, withAreaIcon(this.options.looseProjectsTitle?.() ?? "Projects"),
      context.newID ? { newID: context.newID } : {});
    if (isNoEdit(edits)) return;
    const previous = this.account;
    this.account = applyChanges(previous, changesOf(edits));
    this.backend.apply(changesOf(edits), this.account, previous);
  }

  private remember(title: string, changes: Changes, previous: Account, next: Account): void {
    const step = this.openStep ?? { title, before: {}, after: {} };
    const touched = touchedIDs(changes);
    for (const name of ACCOUNT_COLLECTIONS) {
      const ids = touched[name];
      if (!ids) continue;
      const before = byID<{ id: string }>(previous[name]);
      const after = byID<{ id: string }>(next[name]);
      const stepBefore = (step.before[name] ??= new Map()) as Map<string, unknown>;
      const stepAfter = (step.after[name] ??= new Map()) as Map<string, unknown>;
      for (const id of ids) {
        if (!stepBefore.has(id)) stepBefore.set(id, before.get(id));
        stepAfter.set(id, after.get(id));
      }
    }
    if (changes.favorites !== undefined) {
      step.favorites = { before: step.favorites ? step.favorites.before : previous.favorites, after: next.favorites };
    }
    if (!this.openStep) this.pushUndo(step);
  }

  private pushUndo(step: UndoStep): void {
    this.undoSteps.push(step);
    const limit = this.options.undoLimit ?? 50;
    if (this.undoSteps.length > limit) this.undoSteps.splice(0, this.undoSteps.length - limit);
    this.redoSteps = [];
  }

  /** Puts the records of a step back as they were: only those, and only where they differ from now. */
  private restore(records: TouchedRecords, favorites: Favorite[] | undefined, hasFavorites: boolean): void {
    const changes: Changes = {};
    for (const name of ACCOUNT_COLLECTIONS) {
      const wanted = records[name] as Map<string, { id: string } | undefined> | undefined;
      if (!wanted) continue;
      const now = byID<{ id: string }>(this.account[name]);
      const saved: { id: string }[] = [];
      const deleted: string[] = [];
      for (const [id, record] of wanted) {
        if (record === undefined) {
          if (now.has(id)) deleted.push(id);
        } else if (now.get(id) !== record) {
          saved.push(record);
        }
      }
      if (saved.length > 0 || deleted.length > 0) (changes as Record<string, unknown>)[name] = { saved, deleted };
    }
    if (hasFavorites && favorites !== undefined) changes.favorites = favorites;
    this.commit(changes, { undoable: false });
  }

  /** A change from elsewhere that reached records of a step drops it, so undoing can't undo what someone else did. */
  private dropSteps(remote: Account): void {
    if (this.undoSteps.length === 0 && this.redoSteps.length === 0) return;
    const maps = new Map<AccountCollection, [Map<string, unknown>, Map<string, unknown>]>();
    const differs = (name: AccountCollection, id: string): boolean => {
      let pair = maps.get(name);
      if (!pair) {
        pair = [byID<{ id: string }>(this.account[name]), byID<{ id: string }>(remote[name])];
        maps.set(name, pair);
      }
      const [mine, theirs] = pair;
      const a = mine.get(id);
      const b = theirs.get(id);
      if (a === b) return false;
      if (a === undefined || b === undefined) return true;
      const codec = codecs[name] as { encode(record: unknown): unknown };
      return !sameValue(codec.encode(a), codec.encode(b));
    };
    const touchedElsewhere = (step: UndoStep) =>
      ACCOUNT_COLLECTIONS.some((name) => [...(step.after[name]?.keys() ?? [])].some((id) => differs(name, id)))
      || (step.favorites !== undefined && !sameValue(this.account.favorites, remote.favorites));
    this.undoSteps = this.undoSteps.filter((step) => !touchedElsewhere(step));
    this.redoSteps = this.redoSteps.filter((step) => !touchedElsewhere(step));
  }

  private makeState(): StoreState {
    const status: StoreStatus = !this.loaded ? "loading" : this.backend.isBlocked ? "blocked" : "ready";
    const undo = this.undoSteps.at(-1);
    const redo = this.redoSteps.at(-1);
    const state: StoreState = {
      status,
      account: this.account,
      today: this.context().today,
      canUndo: undo !== undefined,
      canRedo: redo !== undefined,
      suggestsUpdate: suggestsUpdate(accountMarks(this.account.user), containsUnknownValues(this.account)),
      sync: this.syncNow(),
    };
    if (undo) state.undoTitle = undo.title;
    if (redo) state.redoTitle = redo.title;
    return state;
  }

  private emit(): void {
    if (this.transactionDepth > 0) return;
    this.state = this.makeState();
    for (const listener of [...this.listeners]) listener();
  }
}

/** The favorite a list stands for: a project, an area, or a built-in list by its key. */
export function favoriteOf(route: Route, account: Pick<Account, "projects" | "areas">): Favorite | null {
  switch (route.kind) {
    case "project": return account.projects.some((project) => project.id === route.id) ? { kind: "project", id: route.id } : null;
    case "area": return account.areas.some((area) => area.id === route.id) ? { kind: "area", id: route.id } : null;
    case "search": case "template": case "templates": case "place": return null;
    default: return { kind: "list", key: storageKey(route) };
  }
}

/**
 * Every to-do saved here notes the zone its reminders were set in (Store.noteReminderZones in the kit's save): one place
 * for every writer, as on the apps (docs/48).
 */
function withReminderZones(changes: Changes, previous: Account, here: string): Changes {
  const todos = changes.todos;
  if (!todos || todos.saved.length === 0) return changes;
  const before = new Map(previous.todos.map((todo) => [todo.id, todo]));
  return { ...changes, todos: { ...todos, saved: todos.saved.map((todo) => noteReminderZones(before.get(todo.id), todo, here)) } };
}

